/**
 * What "Offline speichern" downloads for the 2D map: the swisstopo WMTS tiles around the entry
 * point, zoom 12 to 17, for both base layers and the bathymetry overlay. Pure maths, no I/O.
 */
import type { LatLon } from "@divespots/contracts";
import { BASE_LAYERS, BATHY_MIN_ZOOM, BATHY_OVERLAY, wmtsTileUrl } from "../map/tiles.ts";
import type { WmtsLayer } from "../map/tiles.ts";

export const SAVE_ZOOMS: readonly number[] = [12, 13, 14, 15, 16, 17];
/** Tiles are saved for a square of this half-width around the entry point. */
export const SAVE_TILE_RADIUS_M = 1000;

export interface TileRef {
  z: number;
  x: number;
  y: number;
}

export interface SaveLayer extends WmtsLayer {
  key: "map" | "aerial" | "bathy";
}

export const SAVE_LAYERS: readonly SaveLayer[] = [
  { key: "map", ...BASE_LAYERS.map },
  { key: "aerial", ...BASE_LAYERS.aerial },
  { key: "bathy", ...BATHY_OVERLAY },
];

/** Typical sizes (bytes) measured on swisstopo tiles around Swiss lakes; the estimate is shown as "ca.". */
export const AVG_TILE_BYTES: Record<SaveLayer["key"], number> = {
  map: 22_000,
  aerial: 42_000,
  bathy: 14_000,
};
export const TERRAIN_BYTES_PER_CELL = 5; // float32 elevation + 1 byte source
/** The swissimage texture: ~1.2 MB at 2048 px (radius 150 m and up), ~0.35 MB at 1024 px. */
export const aerialTextureBytes = (radiusM: number): number =>
  radiusM >= 150 ? 1_200_000 : 350_000;
export const DETAIL_BYTES = 8_000;

const M_PER_DEG_LAT = 111_320;

export const lonToTileX = (lon: number, z: number): number =>
  Math.floor(((lon + 180) / 360) * 2 ** z);

export const latToTileY = (lat: number, z: number): number => {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
};

/** Inclusive tile ranges at zoom `z` covering the square of half-width `radiusM` around `center`. */
export function tileRange(center: LatLon, radiusM: number, z: number) {
  const dLat = radiusM / M_PER_DEG_LAT;
  const dLon = radiusM / (M_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180));
  return {
    x0: lonToTileX(center.lon - dLon, z),
    x1: lonToTileX(center.lon + dLon, z),
    y0: latToTileY(center.lat + dLat, z), // north = smaller y
    y1: latToTileY(center.lat - dLat, z),
  };
}

export function tilesAround(
  center: LatLon,
  radiusM: number = SAVE_TILE_RADIUS_M,
  zooms: readonly number[] = SAVE_ZOOMS,
): TileRef[] {
  const out: TileRef[] = [];
  for (const z of zooms) {
    const { x0, x1, y0, y1 } = tileRange(center, radiusM, z);
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push({ z, x, y });
  }
  return out;
}

/** Layers that exist at zoom `z` (the relief overlay only from BATHY_MIN_ZOOM, up to its native zoom). */
export const layersAt = (z: number): SaveLayer[] =>
  SAVE_LAYERS.filter((l) => z <= l.maxNativeZoom && (l.key !== "bathy" || z >= BATHY_MIN_ZOOM));

/** Every tile URL to store, exactly as Leaflet will request it (these are the cache keys). */
export function tileUrls(
  center: LatLon,
  radiusM: number = SAVE_TILE_RADIUS_M,
  zooms: readonly number[] = SAVE_ZOOMS,
): string[] {
  return tilesAround(center, radiusM, zooms).flatMap(({ z, x, y }) =>
    layersAt(z).map((l) => wmtsTileUrl(l, z, x, y)),
  );
}

export interface SaveEstimate {
  tiles: number;
  /** Terrain + photo + spot detail, 0 when the spot has no lakebed data. */
  terrainBytes: number;
  bytes: number;
}

/** Size of everything a save downloads. `terrainRadiusM` is null for spots without bathymetry. */
export function estimateSaveBytes(
  center: LatLon,
  terrainRadiusM: number | null,
  cellSizeM = 1,
  tileRadiusM: number = SAVE_TILE_RADIUS_M,
): SaveEstimate {
  let tiles = 0;
  let tileBytes = 0;
  for (const { z } of tilesAround(center, tileRadiusM)) {
    for (const l of layersAt(z)) {
      tiles++;
      tileBytes += AVG_TILE_BYTES[l.key];
    }
  }
  const cells = terrainRadiusM === null ? 0 : ((2 * terrainRadiusM) / cellSizeM + 1) ** 2;
  const terrainBytes =
    terrainRadiusM === null
      ? 0
      : cells * TERRAIN_BYTES_PER_CELL + aerialTextureBytes(terrainRadiusM);
  return { tiles, terrainBytes, bytes: Math.round(tileBytes + terrainBytes + DETAIL_BYTES) };
}
